import React, { useCallback, useEffect, useRef, useState } from 'react';
import DualRangeSlider from 'rc-slider/lib/Range';
import {
  classnames as cx,
  currencyFormatter as formatter
} from '../../../utilities';

import 'rc-slider/assets/index.css';
import './RVDualRangeSlider.scss';
import useSliderValidation from './useSliderValidation';

const RVDualRangeSlider = ({
  enforceMin = false,
  enforceMax = false,
  debounceDelay = 0,
  initialValue = [0, 10000],
  leftLabel = 'Min',
  rightLabel = 'Max',
  min = 0,
  max = 10000,
  onUpdate = () => {},
  showTrack = false,
  step = 50,
  eventTrackers = null
}) => {
  const validateMin = value => {
    const tooLow = enforceMin && value < min;
    const tooHigh = value >= max;
    return !tooLow && !tooHigh;
  };
  const validateMax = value => {
    const tooLow = value <= min;
    const tooHigh = enforceMax && value > max;
    return !tooLow && !tooHigh;
  };
  const validationFunc = values => {
    const validationArr = values
      .map((value, index) => {
        value = parseInt(String(value).replace(/[^0-9|.]/g, ''), 10);
        if (Number.isNaN(value)) {
          return false;
        }

        return index === 0 ? validateMin(value) : validateMax(value);
      })
      .filter(Boolean);
    return validationArr.length === 2;
  };

  const { handleUpdate, handleChange, isSliderDirty, values, cleanValues } =
    useSliderValidation({
      initialValue,
      onUpdate,
      validationFunc
    });

  const [isInputFocused, setIsInputFocused] = useState(false);
  const debounceHandler = useRef(null);

  useEffect(() => {
    clearTimeout(debounceHandler.current);
    debounceHandler.current = setTimeout(() => {
      handleUpdate();
    }, debounceDelay);

    return clearTimeout(debounceHandler.current);
  }, [debounceDelay, handleUpdate]);

  const immediatelyUpdateValue = useCallback(() => {
    clearTimeout(debounceHandler.current);
    handleUpdate();
  }, [handleUpdate, debounceHandler]);

  const getIsMinInput = target => target.name === 'min-input';

  const handleInputChange = useCallback(
    ({ target }) => {
      const inputType = target.name;
      if (inputType === 'min-input') {
        handleChange({ min: target.value });
      } else {
        handleChange({ max: target.value });
      }
    },
    [handleChange]
  );

  const handleKeyDown = useCallback(({ key: keyDown, target }) => {
    if (keyDown === 'Enter') {
      target.blur();
    }
  }, []);

  const highlightContents = ({ target }) => {
    target.select();
  };

  const handleFocus = ({ target }) => {
    setIsInputFocused(true);
    const isMinInput = getIsMinInput(target);
    if (isMinInput && eventTrackers?.trackClickedIntoLowerInput) {
      eventTrackers.trackClickedIntoLowerInput();
    }
    if (!isMinInput && eventTrackers?.trackClickedIntoUpperInput) {
      eventTrackers.trackClickedIntoUpperInput();
    }
  };

  const handleBlur = ({ target }) => {
    setIsInputFocused(false);
    if (isSliderDirty) {
      immediatelyUpdateValue();
      const isMinInput = getIsMinInput(target);
      if (isMinInput && eventTrackers?.trackAdjustedLowerInput) {
        eventTrackers.trackAdjustedLowerInput();
      }
      if (!isMinInput && eventTrackers?.trackAdjustedUpperInput) {
        eventTrackers.trackAdjustedUpperInput();
      }
    }
  };

  const handleSliderMove = value => {
    handleChange({ min: value[0], max: value[1] });
  };

  const handleSliderMoveAfter = () => {
    if (
      isSliderDirty &&
      values[0] !== cleanValues[0] &&
      eventTrackers?.trackMovedLowerEndOfSlider
    ) {
      eventTrackers.trackMovedLowerEndOfSlider();
    } else if (
      isSliderDirty &&
      values[1] !== cleanValues[1] &&
      eventTrackers?.trackMovedUpperEndOfSlider
    ) {
      eventTrackers.trackMovedUpperEndOfSlider();
    }
    !isInputFocused && immediatelyUpdateValue();
  };
  
  return (
    <div className="rvdualslider margin-top">
      <div className="rvdualslider__header">
        <div className="rvdualslider__input-labels">
          <label className="rvdualslider__input-label" htmlFor="min-input">
            {leftLabel}
          </label>
          <div className="rvdualslider__spacer" />
          <label className="rvdualslider__input-label" htmlFor="max-input">
            {rightLabel}
          </label>
        </div>
        <div>
          <input
            className="rvdualslider__input center-text bold-text"
            maxLength={8}
            name="min-input"
            onBlur={handleBlur}
            onChange={handleInputChange}
            onClick={highlightContents}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            type={isInputFocused ? 'number' : 'text'}
            value={
              isInputFocused ? values[0] : formatter.format(Number(values[0]))
            }
            width="64"
          />
          <div className="rvdualslider__divider">&nbsp;</div>
          <input
            className="rvdualslider__input center-text bold-text"
            maxLength={8}
            name="max-input"
            onBlur={handleBlur}
            onChange={handleInputChange}
            onClick={highlightContents}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            type={isInputFocused ? 'number' : 'text'}
            value={
              isInputFocused ? values[1] : formatter.format(Number(values[1]))
            }
            width="64"
          />
        </div>
      </div>

      <div
        className={cx('rvdualslider__slider-wrapper', {
          'rvdualslider__slider-wrapper--no-track': !showTrack
        })}
      >
        <DualRangeSlider
          min={min}
          max={max}
          step={step}
          onChange={handleSliderMove}
          value={values}
          onAfterChange={handleSliderMoveAfter}
        />
      </div>
      <div className="rvdualslider__range-labels">
        <div>
          <span>{formatter.format(min)}</span>
          <span>{formatter.format(max)}</span>
        </div>
        <div className="rvdualslider__range-labels-copy">
          <span>Lowest Available</span>
          <span>Highest Available</span>
        </div>
      </div>
    </div>
  );
};

export default RVDualRangeSlider;